import type { Role } from '@/types/domain';

/**
 * Capability-based authorization. Components ask `can(role, capability)` — they
 * never branch on role names directly, so the policy lives in exactly one place
 * and mirrors what the Supabase RLS policies will enforce server-side.
 */
export type Capability =
  | 'overview:view'
  | 'floor:view'
  | 'session:manage'
  | 'billing:settle'
  | 'bookings:manage'
  | 'customers:view'
  | 'food:manage'
  | 'menu:edit'
  | 'rates:edit'
  | 'membership:view'
  | 'membership:manage'
  | 'payments:view'
  | 'expenses:edit'
  | 'reports:view'
  | 'attendance:view'
  | 'attendance:manage'
  | 'users:approve'
  | 'settings:edit';

const ALL: Capability[] = [
  'overview:view',
  'floor:view',
  'session:manage',
  'billing:settle',
  'bookings:manage',
  'customers:view',
  'food:manage',
  'menu:edit',
  'rates:edit',
  'membership:view',
  'membership:manage',
  'payments:view',
  'expenses:edit',
  'reports:view',
  'attendance:view',
  'attendance:manage',
  'users:approve',
  'settings:edit',
];

const MATRIX: Record<Role, Set<Capability>> = {
  MASTER_ADMIN: new Set(ALL),
  ADMIN: new Set(ALL),
  MANAGER: new Set<Capability>([
    'overview:view',
    'floor:view',
    'session:manage',
    'billing:settle',
    'bookings:manage',
    'customers:view',
    'food:manage',
    'menu:edit',
    // rates:edit is admin-only — managers price at the counter, not the rate card.
    'membership:view',
    'membership:manage',
    'payments:view',
    'expenses:edit',
    'reports:view',
    'attendance:view',
    'attendance:manage',
  ]),
  STAFF: new Set<Capability>([
    'floor:view',
    'session:manage',
    'billing:settle',
    'bookings:manage',
    'customers:view',
    'food:manage',
    'membership:view',
    'attendance:view',
  ]),
};

export function can(role: Role | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  return MATRIX[role].has(capability);
}
